<template>
  <div class="fill-height overflow-visible">
    <v-carousel
      v-model="currentItemIndex"
      :continuous="false"
      :touch="false"
      class="overflow-visible fill-height"
      hide-delimiters
      :show-arrows="false"
      direction="vertical"
    >
      <slot />
    </v-carousel>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, provide, reactive, ref, watch } from "vue";
import { useOnboardingStore } from "@/store/onboarding";

const onboardingStore = useOnboardingStore();

const props = defineProps<{ hideProgress?: boolean }>();

const currentItemIndex = ref(0);

interface CarouselItemBase {
  id: string;
  name?: string;
  disabled?: boolean;
  onFocus: () => void;
  handleNextClick: () => void;
}

interface CarouselItem extends CarouselItemBase {
  index: number;
  isFirst: boolean;
  isLast: boolean;
}

export interface CarouselContext {
  currentItem: number;
  items: CarouselItem[];
  // TODO: look into default register and items behaviour on v-carousel
  registerItem(item: CarouselItemBase): void;
  updateItem(itemBase: CarouselItemBase): void;
  goToItem(destination: number): void;
  hideProgress: boolean;
  scrollY: number;
}

const isAndroid = ref(false);

onMounted(() => {
  isAndroid.value = /Android/i.test(navigator.userAgent);
});

const initItems = (items: CarouselItemBase[]): CarouselItem[] =>
  items
    .filter((item) => !item.disabled)
    .map((item, index, array) => {
      return {
        ...item,
        index,
        isFirst: index === 0,
        isLast: index === array.length - 1,
      };
    });

const focusOnItem = (index: number) => {
  carouselContext.items[index].onFocus();
};

const goToItem = (destinationIndex: number) => {
  const restrictedIndex = Math.max(0, Math.min(destinationIndex, carouselContext.items.length - 1));
  currentItemIndex.value = restrictedIndex;
  if (isAndroid.value) {
    setTimeout(() => {
      focusOnItem(restrictedIndex);
    }, 800);
  } else {
    nextTick(() => {
      focusOnItem(restrictedIndex);
    });
  }
};

const goToItemByName = (destinationName: string) => {
  const destinationIndex = carouselContext.items.findIndex((item) => item.name === destinationName);
  if (destinationIndex >= 0) {
    goToItem(destinationIndex);
  }
};

const baseItems = ref<CarouselItemBase[]>([]);

const items = computed(() => initItems(baseItems.value));
const currentTarget = ref<HTMLElement | undefined>(undefined);

const handleFocusChange = (event: FocusEvent) => {
  setTimeout(() => {
    currentTarget.value = event.target as HTMLElement;
  }, 100);
};

const handleKeyup = (event: KeyboardEvent) => {
  if (
    event.key === "Enter" &&
    !currentTarget.value?.classList.contains("form-disable-enter") &&
    !currentTarget.value?.classList.contains("v-list-item") &&
    !currentTarget.value?.getAttribute("input-class")?.includes("form-disable-enter")
  ) {
    items.value[currentItemIndex.value].handleNextClick();
  }
};

const scrollY = ref(0);

const handleScroll = () => {
  scrollY.value = window.scrollY;
};

onMounted(() => {
  window.addEventListener("keyup", handleKeyup);
  window.addEventListener("focusin", handleFocusChange);
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("keyup", handleKeyup);
  window.removeEventListener("focusin", handleFocusChange);
  window.removeEventListener("scroll", handleScroll);
});

watch(
  () => currentItemIndex.value,
  () =>
    setTimeout(() => {
      currentTarget.value?.blur();
      currentTarget.value = undefined;
    }, 100),
);

const carouselContext = reactive<CarouselContext>({
  get currentItem() {
    return currentItemIndex.value;
  },
  get items() {
    return items.value;
  },
  registerItem(item) {
    baseItems.value = [...baseItems.value, item];
  },
  updateItem: (item: CarouselItemBase) => {
    const index = baseItems.value.findIndex((i) => i.id === item.id);
    if (index >= 0) {
      baseItems.value[index] = item;
    }
    baseItems.value = [...baseItems.value];
  },
  goToItem,
  hideProgress: props.hideProgress,
  get scrollY() {
    return scrollY.value;
  },
});

provide("carouselContext", carouselContext);

const setError = (message: string, field?: string) => {
  onboardingStore.setError(message);
  if (field) {
    goToItemByName(field);
  }
};

const clearError = () => {
  onboardingStore.setError(undefined);
};

const next = () => {
  goToItem(currentItemIndex.value + 1);
};

const prev = () => {
  goToItem(currentItemIndex.value - 1);
};

const curr = () => {
  const curr = carouselContext.items[currentItemIndex.value];
  return curr ? curr.name : "";
};

defineExpose({ next, prev, setError, clearError, goToItemByName, curr });
</script>
